import React, { useLayoutEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Stack,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { EntityType, type AnchorEffectAssignValueEntry, type AnchorEntry, type PropertyDefinition, type PropertyDefinitionBelongsTo, type Repository } from '../types';
import { useGetRepositoryByIdQuery } from '../services/repositoryEntries';
import { useAppSelector } from '../hooks';
import { selectIsAuthenticated } from '../containers/authSlice';
import { selectAllAnchorEffects, selectAllPropertyDefinitions } from '../containers/contentSlice';
import { useRepositoryEntryData } from '../hooks/useRepositoryEntryData';

export interface AnchorHookInput {
	/** Present for an existing effect row being kept/edited; absent for a newly added hook. */
	id?: string;
	propertyId: string;
	/** The assigned entry id, known only for existing hooks — shown read-only, recomputed by the caller on save. */
	value?: string;
}

export interface EditAnchorDialogProps {
	open: boolean;
	anchor: AnchorEntry | null;
	repositories: Repository[];
	/** repositoryId of the reference entry the anchor is attached to — used to filter hook property options. */
	anchorReferenceRepositoryId?: string | null;
	onClose: () => void;
	onSubmit: (payload: {
		id: string;
		repositoryId?: string | null;
		direction?: string | null;
		hooks: AnchorHookInput[];
	}) => void;
}

function belongsToPropertiesForCollection(
	collectionRepositoryId: string,
	anchorReferenceRepositoryId: string | null | undefined,
	definitions: PropertyDefinition[],
): PropertyDefinitionBelongsTo[] {
	if (!collectionRepositoryId || !anchorReferenceRepositoryId) {
		return [];
	}
	return definitions.filter(
		(d): d is PropertyDefinitionBelongsTo =>
			d.parentId === collectionRepositoryId
			&& d.type === EntityType.PropertyDefinitionBelongsTo
			&& (d as PropertyDefinitionBelongsTo).targetRepositoryId === anchorReferenceRepositoryId,
	);
}

/** Resolves a repository entry id (a hook's assigned value) into its readable label, online or offline. */
const HookAssignedEntryLabel: React.FC<{ entryId: string }> = ({ entryId }) => {
	const { populatedEntry } = useRepositoryEntryData(true, entryId);
	return <>{populatedEntry?.label || entryId}</>;
};

const EditAnchorDialog: React.FC<EditAnchorDialogProps> = ({
	open,
	anchor,
	repositories,
	anchorReferenceRepositoryId,
	onClose,
	onSubmit,
}) => {
	const [repositoryId, setRepositoryId] = useState('');
	const [directionVertical, setDirectionVertical] = useState(true);
	const [hooks, setHooks] = useState<AnchorHookInput[]>([]);
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);
	const allAnchorEffects = useAppSelector(selectAllAnchorEffects);

	const { data: repositoryById } = useGetRepositoryByIdQuery(repositoryId, {
		skip: !open || !repositoryId || !isAuthenticated,
	});

	const localPropertyDefinitions = useMemo(
		() => allPropertyDefinitions.filter((def) => def.parentId === repositoryId),
		[allPropertyDefinitions, repositoryId],
	);

	const definitionsForHooks = useMemo(
		() => (isAuthenticated ? (repositoryById?.propertyDefinitions ?? []) : localPropertyDefinitions),
		[isAuthenticated, repositoryById?.propertyDefinitions, localPropertyDefinitions],
	);

	const hookPropertyOptions = useMemo(
		() => belongsToPropertiesForCollection(repositoryId, anchorReferenceRepositoryId, definitionsForHooks),
		[repositoryId, anchorReferenceRepositoryId, definitionsForHooks],
	);

	const propertyLabelById = useMemo(
		() => new Map(definitionsForHooks.map((def) => [def.id, def.label])),
		[definitionsForHooks],
	);

	useLayoutEffect(() => {
		if (!open || !anchor) {
			return;
		}
		setRepositoryId(anchor.repositoryId ?? '');
		setDirectionVertical(anchor.direction !== 'horizontal');
		const existingEffects = allAnchorEffects.filter((effect: AnchorEffectAssignValueEntry) => effect.anchorId === anchor.id);
		setHooks(existingEffects.map((effect) => ({ id: effect.id, propertyId: effect.propertyId, value: effect.value })));
	}, [anchor, open]);

	const handleAddHook = () => {
		setHooks((prev) => [...prev, { propertyId: '' }]);
	};

	const handleRemoveHook = (index: number) => {
		setHooks((prev) => prev.filter((_, i) => i !== index));
	};

	const handleHookPropertyChange = (index: number, propertyId: string) => {
		setHooks((prev) => prev.map((hook, i) => (i === index ? { ...hook, propertyId } : hook)));
	};

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (!anchor) {
			return;
		}
		onSubmit({
			id: anchor.id,
			repositoryId: repositoryId || null,
			direction: directionVertical ? 'vertical' : 'horizontal',
			hooks: hooks.filter((hook) => hook.propertyId),
		});
	};

	const canAddHook = Boolean(repositoryId) && hookPropertyOptions.length > 0;

	return (
		<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth scroll="body">
			<form onSubmit={handleSubmit} autoComplete="off">
				<DialogTitle>Edit anchor</DialogTitle>
				<DialogContent>
					<Box display="flex" flexDirection="column" gap={2} mt={1}>
						<FormControl fullWidth size="small">
							<InputLabel id="edit-anchor-repository-label">Collection</InputLabel>
							<Select
								labelId="edit-anchor-repository-label"
								label="Collection"
								value={repositoryId}
								onChange={(e) => setRepositoryId(String(e.target.value))}
							>
								<MenuItem value="">
									<em>None</em>
								</MenuItem>
								{repositories.map((r) => (
									<MenuItem key={`anchor-repo-${r.id}`} value={r.id}>
										{r.title}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl component="fieldset" variant="standard" sx={{ m: 0 }}>
							<FormLabel component="legend" sx={{ typography: 'body2', color: 'text.primary', mb: 0.5 }}>
								Direction
							</FormLabel>
							<RadioGroup
								row
								value={directionVertical ? 'vertical' : 'horizontal'}
								onChange={(e) => setDirectionVertical(e.target.value === 'vertical')}
							>
								<FormControlLabel value="vertical" control={<Radio size="small" />} label="Vertical" />
								<FormControlLabel value="horizontal" control={<Radio size="small" />} label="Horizontal" />
							</RadioGroup>
						</FormControl>
						{anchor?.referenceId && <Box display="flex" flexDirection="column" gap={1}>
							<FormLabel component="legend" sx={{ typography: 'body2', color: 'text.primary' }}>
								Hooks
							</FormLabel>
							{hooks.length === 0 && (
								<Typography variant="body2" sx={{ color: 'text.secondary' }}>
									No hooks configured. Add one to assign a property value when something snaps here.
								</Typography>
							)}
							<Stack gap={1}>
								{hooks.map((hook, index) => (
									<Stack key={`anchor-hook-${index}`} direction="row" gap={1} alignItems="center">
										{hook.id ? (
											<Box
												flex={1}
												sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 1 }}
											>
												<Typography variant="body2">
													Assign <strong>{hook.value ? <HookAssignedEntryLabel entryId={hook.value} /> : '(unknown entry)'}</strong> as{' '}
													<strong>{propertyLabelById.get(hook.propertyId) ?? hook.propertyId}</strong>
												</Typography>
											</Box>
										) : (
											<FormControl fullWidth size="small">
												<InputLabel id={`edit-anchor-hook-property-label-${index}`}>Assign value to property</InputLabel>
												<Select
													labelId={`edit-anchor-hook-property-label-${index}`}
													label="Assign value to property"
													value={hook.propertyId}
													onChange={(e) => handleHookPropertyChange(index, String(e.target.value))}
													disabled={!canAddHook}
												>
													{hookPropertyOptions.map((d) => (
														<MenuItem key={`hook-prop-${d.id}`} value={d.id}>
															{d.label}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										)}
										<IconButton size="small" onClick={() => handleRemoveHook(index)} aria-label="Remove hook">
											<DeleteOutlineIcon fontSize="small" />
										</IconButton>
									</Stack>
								))}
							</Stack>
							<span style={{ alignSelf: 'flex-start' }}>
								<Button
									type="button"
									size="small"
									startIcon={<AddIcon />}
									onClick={handleAddHook}
									disabled={!canAddHook}
								>
									Add hook
								</Button>
							</span>
						</Box>}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button type="button" onClick={onClose}>
						Cancel
					</Button>
					<Button type="submit" variant="contained">
						Save
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default EditAnchorDialog;
